'use client'

import * as React from 'react'
import Image from 'next/image'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn, proxyImageUrl } from '@/lib/utils'
import { useUIStore, useSearchStore } from '@/lib/store'
import { ListingImagePlaceholder } from '@/components/listings'

export function PhotoGallery() {
  const {
    galleryOpen,
    galleryListingId,
    galleryImageIndex,
    galleryImages,
    closeGallery,
    setGalleryImageIndex
  } = useUIStore()
  const { results } = useSearchStore()

  const listing = React.useMemo(() => {
    if (!galleryListingId || galleryImages) return null
    return results.find((r) => r.id === galleryListingId)
  }, [galleryListingId, galleryImages, results])

  const [imageErrors, setImageErrors] = React.useState<Set<number>>(new Set())

  // Reset errors when gallery opens with new images
  React.useEffect(() => {
    if (galleryOpen) {
      setImageErrors(new Set())
    }
  }, [galleryOpen, galleryListingId])

  const images = galleryImages || listing?.images || []
  const currentImage = images[galleryImageIndex]
  
  // Keyboard navigation
  React.useEffect(() => {
    if (!galleryOpen) return
    
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeGallery()
          break
        case 'ArrowLeft':
          if (galleryImageIndex > 0) {
            setGalleryImageIndex(galleryImageIndex - 1)
          }
          break
        case 'ArrowRight':
          if (galleryImageIndex < images.length - 1) {
            setGalleryImageIndex(galleryImageIndex + 1)
          }
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [galleryOpen, galleryImageIndex, images.length, closeGallery, setGalleryImageIndex])
  
  // Prevent body scroll when gallery is open
  React.useEffect(() => {
    if (galleryOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [galleryOpen])
  
  if (!galleryOpen || !currentImage) return null
  
  const goToPrevious = () => {
    if (galleryImageIndex > 0) {
      setGalleryImageIndex(galleryImageIndex - 1)
    }
  }
  
  const goToNext = () => {
    if (galleryImageIndex < images.length - 1) {
      setGalleryImageIndex(galleryImageIndex + 1)
    }
  }
  
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={closeGallery}
    >
      {/* Close button */}
      <button
        onClick={closeGallery}
        className="absolute top-4 left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
      >
        <X className="h-6 w-6" />
      </button>
      
      {/* Image counter */}
      <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/10 text-white text-sm z-10">
        {galleryImageIndex + 1} / {images.length}
      </div>
      
      {/* Previous button */}
      {galleryImageIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            goToPrevious()
          }}
          className={cn(
            'absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full',
            'bg-white/10 hover:bg-white/20 text-white transition-colors z-10'
          )}
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
      )}
      
      {/* Image - NO ANIMATION */}
      <div
        className="relative w-full h-full max-w-6xl max-h-[90vh] mx-auto px-16"
        onClick={(e) => e.stopPropagation()}
      >
        {imageErrors.has(galleryImageIndex) ? (
          <ListingImagePlaceholder variant="large" />
        ) : (
          <Image
            key={currentImage.url} // Force remount for instant switch
            src={proxyImageUrl(currentImage.url)}
            alt={currentImage.alt_text || listing?.title || 'Listing photo'}
            fill
            className="object-contain"
            sizes="100vw"
            priority
            onError={() => setImageErrors(prev => new Set(prev).add(galleryImageIndex))}
          />
        )}
      </div>
      
      {/* Next button */}
      {galleryImageIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            goToNext()
          }}
          className={cn(
            'absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full',
            'bg-white/10 hover:bg-white/20 text-white transition-colors z-10'
          )}
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      )}
      
      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 rounded-lg z-10">
          {images.slice(0, 10).map((img, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation()
                setGalleryImageIndex(index)
              }}
              className={cn(
                'relative w-12 h-12 rounded overflow-hidden',
                index === galleryImageIndex && 'ring-2 ring-white'
              )}
            >
              {imageErrors.has(index) ? (
                <ListingImagePlaceholder variant="small" />
              ) : (
                <Image
                  src={proxyImageUrl(img.url)}
                  alt=""
                  fill
                  className="object-cover"
                  sizes="48px"
                  onError={() => setImageErrors(prev => new Set(prev).add(index))}
                />
              )}
            </button>
          ))}
          {images.length > 10 && (
            <div className="w-12 h-12 flex items-center justify-center text-white text-sm">
              +{images.length - 10}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

